'use client';

import React, { useEffect } from 'react';
import HomepageInteractive from './components/HomepageInteractive';

const Homepage = () => {
  useEffect(() => {
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    const elements = document.querySelectorAll('.scroll-reveal');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const testimonials = [
  {
    id: 1,
    quote: "Samkit Agro's consistent quality and reliable delivery schedules have made them our preferred supplier for organic fertilizers. Their export documentation is always flawless.",
    author: "Michael Chen",
    company: "AgriTrade International",
    country: "Singapore"
  },
  {
    id: 2,
    quote: "We\'ve been sourcing agricultural commodities from Samkit Agro for three years. Their bulk pricing and quality assurance processes are unmatched in the industry.",
    author: "Sarah Williams",
    company: "Global Harvest Ltd",
    country: "United Kingdom"
  },
  {
    id: 3,
    quote: "The team at Samkit Agro understands international compliance requirements perfectly. Every shipment arrives on time with complete certification.",
    author: "Rajesh Kumar",
    company: "Emirates Trading Co",
    country: "UAE"
  }];


  const certifications = [
  {
    id: 1,
    name: "Cow Dung Fertilizer Compost Analysis",
    description: "Laboratory analysis showing nutrient content and safety parameters for cow dung fertilizer compost sample",
    icon: "BeakerIcon",
    image: "/assets/images/WhatsApp_Image_2026-01-14_at_20.42.19-1768452557900.jpeg",
    alt: "Certificate of Analysis from Sheetal Analytical Laboratory for cow dung fertilizer compost sample showing quality testing results and nutrient analysis"
  },
  {
    id: 2,
    name: "Cow Dung Machine Work Sample",
    description: "Quality testing and validation report for organic fertilizer production process using machine processing",
    icon: "BeakerIcon",
    image: "/assets/images/WhatsApp_Image_2026-01-14_at_20.42.20_1_-1768452644824.jpeg",
    alt: "Certificate of Analysis from Sheetal Analytical Laboratory for cow dung machine work sample showing comprehensive quality testing results"
  },
  {
    id: 3,
    name: "Fertilizer Value & Heavy Metals Testing",
    description: "Detailed analysis page showing fertilizer value parameters and heavy metals testing results with authorized signatures",
    icon: "BeakerIcon",
    image: "/assets/images/WhatsApp_Image_2026-01-14_at_20.42.20_2_-1768452747276.jpeg",
    alt: "Certificate of Analysis page 2 from Sheetal Analytical Laboratory showing fertilizer value parameters and heavy metals testing results with authorized signatures"
  },
  {
    id: 4,
    name: "Organic Manure Sample 4 Analysis",
    description: "Comprehensive quality testing and validation report for organic manure Sample 4 demonstrating product quality",
    icon: "BeakerIcon",
    image: "/assets/images/WhatsApp_Image_2026-01-14_at_20.42.20_3_-1768452825641.jpeg",
    alt: "Certificate of Analysis from Sheetal Analytical Laboratory for organic manure Sample 4 showing comprehensive quality testing results and laboratory validation"
  },
  {
    id: 5,
    name: "Detailed Quality Testing Documentation",
    description: "Complete laboratory validation results and quality testing documentation for organic fertilizer products",
    icon: "BeakerIcon",
    image: "/assets/images/WhatsApp_Image_2026-01-14_at_20.42.20-1768452914551.jpeg",
    alt: "Certificate of Analysis from Sheetal Analytical Laboratory showing detailed quality testing documentation and laboratory validation results for organic fertilizer products"
  },
  {
    id: 6,
    name: "Final Analysis Report with Signatures",
    description: "Final page of Certificate of Analysis showing fertilizer parameters and heavy metals testing with authorized signatures",
    icon: "BeakerIcon",
    image: "/assets/images/WhatsApp_Image_2026-01-14_at_20.42.21-1768453012501.jpeg",
    alt: "Certificate of Analysis final page from Sheetal Analytical Laboratory showing fertilizer value parameters and heavy metals testing results with authorized signatures for complete quality testing documentation"
  }];


  const products = [
  {
    id: 1,
    name: "Organic Fertilizers",
    description: "Premium quality organic fertilizers and bio-fertilizers for sustainable agriculture. Certified products that enhance soil health and crop yield.",
    minOrder: "1 tonne",
    packaging: "50kg bags, bulk containers",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_104d11a2a-1764677535204.png",
    alt: "Organic fertilizer bags stacked for agricultural use",
    icon: "BeakerIcon"
  },
  {
    id: 2,
    name: "Agricultural Commodities",
    description: "Wide range of agricultural products including grains, pulses, and spices. Sourced from certified farms with complete quality documentation.",
    minOrder: "5 tonnes",
    packaging: "Custom packaging available",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_113e88f66-1767887431241.png",
    alt: "Various agricultural grains and spices in bulk display",
    icon: "CubeIcon"
  },
  {
    id: 3,
    name: "Puja Products",
    description: "Traditional puja items and religious products. Authentic materials sourced ethically with respect for cultural significance.",
    minOrder: "100 kg",
    packaging: "Retail and bulk packaging",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13453b3b2-1767887430070.png",
    alt: "Traditional puja items including incense and religious materials",
    icon: "SparklesIcon"
  }];


  const markets = [
  {
    id: 1,
    region: "Middle East",
    countries: ["UAE", "Saudi Arabia", "Qatar", "Oman"],
    icon: "GlobeAltIcon",
    stats: "40% of exports"
  },
  {
    id: 2,
    region: "Southeast Asia",
    countries: ["Singapore", "Malaysia", "Indonesia"],
    icon: "GlobeAsiaAustraliaIcon",
    stats: "30% of exports"
  },
  {
    id: 3,
    region: "Europe",
    countries: ["UK", "Germany", "Netherlands"],
    icon: "GlobeEuropeAfricaIcon",
    stats: "20% of exports"
  },
  {
    id: 4,
    region: "North America",
    countries: ["USA", "Canada"],
    icon: "GlobeAmericasIcon",
    stats: "10% of exports"
  }];


  const whatsappNumber = "+917304538491";

  return (
    <div data-component-id="src/app/homepage/page.tsx:169:4" data-component-path="src/app/homepage/page.tsx" data-component-line="169" data-component-end-line="177" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <HomepageInteractive data-component-id="src/app/homepage/page.tsx:170:6" data-component-path="src/app/homepage/page.tsx" data-component-line="170" data-component-end-line="175" data-component-file="page.tsx" data-component-name="HomepageInteractive" data-component-content="%7B%22elementName%22%3A%22HomepageInteractive%22%7D"
        testimonials={testimonials}
        certifications={certifications}
        products={products}
        markets={markets}
        whatsappNumber={whatsappNumber} />

    </div>);

};

export default Homepage;